import { useEffect, useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import PasswordInput from "@/components/PasswordInput";
import GoogleSignInButton from "@/components/GoogleSignInButton";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const from = location.state?.from || "/";

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const cleanLoginPassword = (value) =>
    (value || "")
      .replace(/[\u200B\u200C\u200D\uFEFF]/g, "")
      .trim();

  // Surface Google sign-in errors passed back via ?google_error=... from
  // the /api/auth/google/callback redirect. Once shown, strip the param
  // so a refresh doesn't repeat it.
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const g = params.get("google_error");
    if (g) {
      setError(g);
      const cleaned = location.pathname;
      window.history.replaceState(null, "", cleaned);
    }
  }, [location.search, location.pathname]);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      await login(email.trim().toLowerCase(), cleanLoginPassword(password));
      navigate(from, { replace: true });
    } catch (err) {
      setError(err.message || "Login failed.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-6 bg-[var(--bg)] text-[var(--text)]">
      <div className="w-full max-w-sm">
        <div className="text-center mb-10">
          <h1 className="font-mono-ui text-[11px] tracking-[0.4em] uppercase text-[var(--text-muted)] mb-4">
            Humane Behavior AI
          </h1>
          <p className="font-serif italic text-3xl leading-tight">Sign in.</p>
          <p className="font-sans text-sm text-[var(--text-muted)] mt-3">
            Continue where you left off.
          </p>
        </div>

        <form onSubmit={onSubmit} className="space-y-5" data-testid="login-form">
          <GoogleSignInButton testid="login-google-btn" />

          <div className="flex items-center gap-3 py-1" aria-hidden="true">
            <span className="flex-1 h-px bg-[var(--border)]" />
            <span className="font-mono-ui text-[9px] tracking-[0.3em] uppercase text-[var(--text-dim)]">
              or with email
            </span>
            <span className="flex-1 h-px bg-[var(--border)]" />
          </div>

          <label className="block">
            <span className="font-mono-ui text-[10px] tracking-[0.3em] uppercase text-[var(--text-dim)]">
              Email
            </span>
            <input
              type="email"
              required
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              data-testid="login-email"
              className="mt-2 w-full bg-transparent border-b border-[var(--border)] focus:border-[var(--accent)] outline-none py-2 text-base transition-colors"
            />
          </label>

          <label className="block">
            <span className="font-mono-ui text-[10px] tracking-[0.3em] uppercase text-[var(--text-dim)]">
              Password
            </span>
            <PasswordInput
              required
              autoComplete="current-password"
              minLength={1}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              data-testid="login-password"
              eyeTestId="login-password-toggle"
              wrapperClassName="mt-2"
              className="text-base"
            />
          </label>

          {error && (
            <p
              data-testid="login-error"
              className="font-sans text-sm text-red-400 border-l-2 border-red-400 pl-3"
            >
              {error}
            </p>
          )}

          <button
            type="submit"
            disabled={busy}
            data-testid="login-submit"
            className="w-full py-3 mt-2 font-mono-ui text-[11px] tracking-[0.3em] uppercase border border-[var(--accent)] text-[var(--accent)] hover:bg-[var(--accent)] hover:text-[var(--bg)] transition-colors disabled:opacity-40"
          >
            {busy ? "Signing in…" : "Sign in"}
          </button>

          <div className="text-center pt-1">
            <Link
              to="/forgot-password"
              data-testid="login-forgot"
              className="font-mono-ui text-[10px] tracking-[0.24em] uppercase text-[var(--text-dim)] hover:text-[var(--accent)] transition-colors"
            >
              Forgot password?
            </Link>
          </div>
        </form>

        <p className="font-sans text-sm text-[var(--text-muted)] text-center mt-8">
          No account?{" "}
          <Link
            to="/register"
            data-testid="login-to-register"
            className="text-[var(--accent)] hover:underline"
          >
            Create one.
          </Link>
        </p>

        <p className="font-mono-ui text-[10px] tracking-[0.22em] uppercase text-[var(--text-dim)] text-center mt-6">
          Need help?{" "}
          <a
            href="mailto:support@humanebehavior.com"
            data-testid="support-email"
            className="hover:text-[var(--accent)] transition-colors"
          >
            support@humanebehavior.com
          </a>
        </p>

        <p className="font-mono-ui text-[9px] tracking-[0.24em] uppercase text-[var(--text-dim)] text-center mt-3">
          By continuing you agree to our{" "}
          <Link to="/terms" data-testid="login-terms-link" className="hover:text-[var(--accent)] transition-colors">
            Terms
          </Link>
          {" · "}
          <Link to="/privacy" data-testid="login-privacy-link" className="hover:text-[var(--accent)] transition-colors">
            Privacy
          </Link>
        </p>
      </div>
    </div>
  );
}
